import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Sparkles, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SITE_URL } from "@/lib/site";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * "My readings" — buyers who lost their confirmation email can pop their
 * address in here and we'll email a fresh list of links to every paid
 * reading tied to that address. Never confirms whether an email exists in
 * our system (privacy).
 */
export default function MyReadingsPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/readings/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, origin_url: SITE_URL }),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
      toast.success("If we have readings for that email, they're on their way.");
    } catch {
      toast.error("Something went wrong — please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10">
      <header className="sticky top-0 z-30 backdrop-blur-md border-b border-[color:var(--gold)]/12" style={{ background: "rgba(5,9,23,0.72)" }}>
        <div className="mx-auto max-w-[900px] px-6 md:px-10 py-4 md:py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90" data-testid="my-readings-back">
            <ArrowLeft size={14} className="text-[color:var(--gold)]" />
            <span className="label-overline">Back to Earth Pulse</span>
          </Link>
          <Sparkles size={14} className="text-[color:var(--gold)]" />
        </div>
      </header>

      <main className="mx-auto max-w-[640px] px-6 md:px-10 py-16 md:py-24" data-testid="my-readings-page">
        <div className="label-overline">Your reading library</div>
        <h1 className="mt-4 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.05] tracking-tight">
          Lost the <span className="font-serif-italic text-[color:var(--gold)]">link?</span>
        </h1>
        <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg leading-relaxed">
          Enter the email you used at checkout and we&apos;ll re-send every reading tied to it. Each link opens the reading back in the app — no account or password needed.
        </p>

        {done ? (
          <div
            className="mt-12 rounded-2xl border border-[color:var(--gold)]/40 p-8 text-center"
            style={{ background: "linear-gradient(135deg, rgba(212,162,76,0.08), rgba(229,168,124,0.03))" }}
            data-testid="my-readings-done"
          >
            <Check size={22} className="text-[color:var(--gold)] mx-auto" />
            <h2 className="mt-4 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl">
              On its way.
            </h2>
            <p className="mt-4 font-serif-italic text-[color:var(--parchment-soft)] leading-relaxed">
              If we have any paid readings for <strong className="text-[color:var(--parchment)] not-italic font-medium">{email}</strong>, we&apos;ve just emailed them to you.
              Please check your inbox (and spam folder). If nothing arrives in a few minutes, no readings are tied to that address on our system.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
              data-testid="my-readings-return-home"
            >
              Back to your guidance
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="my-readings-form">
            <div>
              <div className="label-overline mb-2">Your email</div>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="the address you used at checkout"
                required
                className="bg-white border-slate-300 rounded-lg h-12 text-slate-900 font-sans-body"
                data-testid="my-readings-email"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !email}
              className="w-full h-12 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium disabled:opacity-40"
              data-testid="my-readings-submit"
            >
              <Mail size={14} className="mr-2" />
              {submitting ? "Sending…" : "Email me my reading links"}
            </Button>
            <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs text-center">
              We never confirm whether an email exists on our system — this is intentional, for your privacy.
            </p>
          </form>
        )}

        <p className="mt-14 font-serif-italic text-[color:var(--text-muted)] text-sm text-center">
          Haven&apos;t bought one yet?{" "}
          <Link to="/subscribe" className="text-[color:var(--gold)] hover:underline">
            Start your membership · £8.88 / month
          </Link>
        </p>
      </main>
    </div>
  );
}
